import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowRight, Upload, SkipForward, Globe, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { getCase, reverseImageSearch, formatCaseId } from "@/lib/api";
import { SkeletonBlock } from "@/components/Skeletons";

export default function SourceSelection() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const localThumbnail = location.state?.localThumbnail || null;

  const [caseData, setCaseData] = useState(null);
  const [sources, setSources] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    setErr(null);
    Promise.all([getCase(caseId), reverseImageSearch(caseId)])
      .then(([c, s]) => {
        if (!active) return;
        setCaseData(c);
        setSources(s);
      })
      .catch((e) => active && setErr(e.message || "Failed to load source candidates"));
    return () => { active = false; };
  }, [caseId]);

  const thumbnail = caseData?.thumbnail || localThumbnail || null;
  const displayId = caseData ? formatCaseId(caseData.id) : formatCaseId(caseId);

  const confirmSource = async () => {
    if (!selected) {
      toast.error("Choose a source or skip.");
      return;
    }
    setConfirming(true);
    try {
      const token = (() => {
        try {
          const raw = localStorage.getItem("sb-trokiiuteplbdowlypdi-auth-token");
          return raw ? JSON.parse(raw)?.access_token || "" : "";
        } catch { return ""; }
      })();

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "https://app.yoobikwitus.com/api"}/source/select`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            analysis_id: caseId,
            source_url: selected.url,
          }),
        },
      );

      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || b.message || `Source confirm failed (${res.status})`);
      }

      toast.success("Source confirmed", { description: selected.sourceDomain });
      navigate(`/report/${caseId}`, { state: { localThumbnail } });
    } catch (e) {
      toast.error("Could not save source", { description: e.message });
      // Navigate anyway so the user is not stuck
      navigate(`/report/${caseId}`, { state: { localThumbnail } });
    } finally {
      setConfirming(false);
    }
  };

  const skip = () => {
    toast.message("Skipped source match", { description: "Proceeding to metadata report." });
    navigate(`/report/${caseId}`, { state: { localThumbnail } });
  };

  const uploadSource = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setConfirming(true);
    try {
      const token = (() => {
        try {
          const raw = localStorage.getItem("sb-trokiiuteplbdowlypdi-auth-token");
          return raw ? JSON.parse(raw)?.access_token || "" : "";
        } catch { return ""; }
      })();

      const formData = new FormData();
      formData.append("file", file);
      formData.append("analysis_id", caseId);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "https://app.yoobikwitus.com/api"}/source/upload?analysis_id=${caseId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || b.message || `Source upload failed (${res.status})`);
      }

      toast.success("Source file received", { description: "Proceeding to metadata report." });
      navigate(`/report/${caseId}`, { state: { localThumbnail } });
    } catch (e) {
      toast.error("Could not upload source", { description: e.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="source-selection-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
            Step 2 of 3
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Select likely source
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Candidate matches from a reverse image search. Pick the one that most likely
            originated the asset, upload a source file directly, or skip to metadata analysis.
          </p>
        </div>
        {caseData && (
          <div className="flex items-center gap-3">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt=""
                className="h-14 w-20 rounded-md object-cover ring-1 ring-[#272A30]"
              />
            ) : (
              <div className="h-14 w-20 rounded-md bg-[#1A1D24] ring-1 ring-[#272A30] flex items-center justify-center">
                <ImageIcon className="h-5 w-5 text-zinc-600" />
              </div>
            )}
            <div>
              <div className="text-sm font-medium">
                {caseData.title || caseData.fileName || displayId}
              </div>
              <div className="text-[11px] text-zinc-500 mono">{displayId}</div>
            </div>
          </div>
        )}
      </header>

      {err && (
        <div
          role="alert"
          data-testid="source-error"
          className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3"
        >
          {err}
        </div>
      )}

      {!sources ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="source-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-[#272A30] bg-[#111318] p-3">
              <SkeletonBlock className="aspect-video w-full" />
              <SkeletonBlock className="mt-3 h-3 w-3/4" />
              <SkeletonBlock className="mt-2 h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : sources.length === 0 ? (
        <div
          className="rounded-xl border border-[#272A30] bg-[#111318] p-10 text-center"
          data-testid="source-empty"
        >
          <div className="text-zinc-300 font-medium">No similar images found online</div>
          <p className="text-sm text-zinc-500 mt-2">
            You can still upload a source file directly or skip to metadata analysis.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="source-grid">
          {sources.map((s) => {
            const isSelected = selected?.id === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`source-candidate-${s.id}`}
                className={`text-left rounded-xl border bg-[#111318] overflow-hidden transition-all ${
                  isSelected
                    ? "border-sky-500/60 ring-2 ring-sky-500/30"
                    : "border-[#272A30] hover:border-zinc-500"
                }`}
              >
                <div className="relative aspect-video bg-[#0A0A0B]">
                  {s.thumbnail ? (
                    <img src={s.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Globe className="h-6 w-6 text-zinc-700" />
                    </div>
                  )}
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/70 px-2 py-0.5 text-[11px] font-medium tabular-nums text-white backdrop-blur">
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      s.similarity >= 85 ? "bg-emerald-400"
                      : s.similarity >= 65 ? "bg-sky-400"
                      : s.similarity >= 50 ? "bg-amber-400"
                      : "bg-zinc-400"
                    }`} />
                    {s.similarity}%
                  </span>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 mono">
                    {s.favicon ? (
                      <img src={s.favicon} alt="" className="h-3 w-3" />
                    ) : (
                      <Globe className="h-3 w-3" />
                    )}
                    {s.sourceDomain}
                  </div>
                  {s.capturedAt && (
                    <div className="mt-1 text-[11px] text-zinc-500">
                      Source found {new Date(s.capturedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
        <div className="flex items-center gap-2">
          <input
            id="upload-source-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={uploadSource}
          />
          <button
            onClick={() => document.getElementById("upload-source-input").click()}
            className="inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-sm text-zinc-300 hover:bg-[#1A1D24]"
            data-testid="upload-source-button"
          >
            <Upload className="h-4 w-4" /> Upload source file
          </button>
          <button
            onClick={skip}
            className="inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-sm text-zinc-300 hover:bg-[#1A1D24]"
            data-testid="skip-source-button"
          >
            <SkipForward className="h-4 w-4" /> Skip
          </button>
        </div>
        <button
          onClick={confirmSource}
          disabled={!selected || confirming}
          data-testid="confirm-source-button"
          className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-5 py-2.5 hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {confirming ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Confirming…</>
          ) : (
            <>Continue to metadata report <ArrowRight className="h-4 w-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}
